'use client';
import type { ToastType } from './toast.config';
import { toast } from 'sonner';

import { Icon } from '@/components/entities/Sprite/Icon';
import { cn } from '@/utils/cn';
import { Button } from '../Button/button';
import { getToastBaseOptions, getToastConfig, toastMotionStyle } from './toast.config';

interface ToastProps {
  id?: string | number;
  type: ToastType;
  description?: React.ReactNode;
  title?: string;
  onAccept?: () => void;
  onReject?: () => void;
  acceptButtonText?: string;
  rejectButtonText?: string;
  disableTimer?: boolean;
  containerClassName?: string;
  hideCloseButton?: boolean;
}

const Toast = ({
  id,
  type,
  title,
  description,
  onAccept,
  onReject,
  acceptButtonText,
  rejectButtonText,
  disableTimer,
  containerClassName,
  hideCloseButton,
}: ToastProps) => {
  const toastId = id ?? crypto.randomUUID();
  const config = getToastConfig(type, title, description);

  toast.custom(t => (
    <div
      className={cn(
        'w-96 max-sm:w-[calc(100vw-1.25rem)] pl-4 py-3 pr-3 flex gap-3.5 justify-start items-start relative outline-none ring-0 z-1',
        containerClassName,
      )}
    >
      <div className="flex items-center justify-center relative w-5 h-5 mt-1">
        <Icon
          name={config.iconName}
          className="size-5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        />
      </div>

      <div className="relative flex flex-col w-full items-start gap-1">
        <div className="flex w-full items-center gap-4 justify-between">
          <p className="text-white paragraph-m-medium tracking-[0.0344rem]">{config.title}</p>
          {!hideCloseButton && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toast.dismiss(t);
              }}
            >
              <Icon name="x_close" className="text-white" />
            </button>
          )}
        </div>

        {config.description && (
          <div className="paragraph-s-regular text-white w-5/6 tracking-[0.0344rem] whitespace-pre-wrap wrap-break-word">
            {config.description}
          </div>
        )}

        {(onReject || rejectButtonText || onAccept) && (
          <>
            <div className="flex gap-1.5 mt-3 w-[90%]">
              {(onReject || rejectButtonText) && (
                <>
                  <Button
                    type="button"
                    variant="stroked"
                    size="sm"
                    className="paragraph-s-semibold text-white flex items-center justify-center rounded-[6.25rem] py-1.5 flex-1"
                    onClick={() => {
                      onReject?.();
                      toast.dismiss(t);
                    }}
                  >
                    {rejectButtonText}
                  </Button>
                </>
              )}
              {onAccept && (
                <>
                  <Button
                    type="button"
                    variant="primary"
                    className="rounded-[6.25rem] paragraph-s-semibold py-1.5 flex-1"
                    onClick={() => {
                      onAccept();
                      toast.dismiss(t);
                    }}
                  >
                    {acceptButtonText}
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  ), {
    id: toastId,
    ...getToastBaseOptions(disableTimer),
    // Apply to the Framer Motion wrapper (Sonner) so blur appears immediately.
    className: cn('glass-notify-border overflow-hidden z-10000'),
    style: toastMotionStyle,
    classNames: {
      toast: 'group toast group-[.toaster]:pointer-events-auto',
    },
  });
};

export default Toast;
